import { inject, Injectable } from '@angular/core';

import { type CanvasElement } from '@blaze-runtime/engine-domain';
import { createWorkflowNode, getWorkflowNodeBase } from '@blaze-runtime/workflow-domain';
import { BULK_ACTION_IDS_PROP, getActiveBinding } from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  BULK_ACTION_NODE_ID,
  BULK_EXECUTE_NODE_ID,
  type BulkActionType,
  type DeleteWorkflowNode,
  type DownloadWorkflowNode,
  type ExecuteWorkflowNode,
  getInput1DisplayInfo,
  type SaveFileWorkflowNode,
  type UpdateWorkflowNode,
  type WorkflowNode,
} from '@blaze-shared/workflow-domain';

import { hasDataProps } from '../../canvas-elements/element-model/element-props-guards';
import { isElementType } from '../../canvas-elements/element-model/element-type-guards';

/**
 * Builds the workflow behind a bulk action — what happens when the user picks rows in a table and
 * applies an action to all of them.
 *
 * Every bulk action starts by re-running the container's own data workflow, filtered to the picked
 * rows; the action node then follows and acts on what that returns.
 */
@Injectable({ providedIn: 'root' })
export class BulkActionNodesService {
  private readonly schemaService = inject(SchemaService);

  /**
   * @throws when the element has no workflow-backed data source, which is what the action reruns.
   */
  getBulkActionNodes(
    actionType: BulkActionType,
    element: CanvasElement | undefined,
  ): WorkflowNode[] {
    const containerDataSource = hasDataProps(element)
      ? getActiveBinding(element.props.data, 'value')?.dataSource
      : undefined;
    if (!containerDataSource) {
      throw new Error('Could not create node: data source not configured');
    }
    if (containerDataSource.sourceType !== 'context') {
      throw new Error('Could not create node: non workflow datasource');
    }

    const executeNode: ExecuteWorkflowNode = {
      ...getWorkflowNodeBase('ExecuteWorkflow', BULK_EXECUTE_NODE_ID),
      workflowId: containerDataSource.id,
      filterInputName: BULK_ACTION_IDS_PROP,
      hasActionNode: false,
      workflowType: 'Regular',
      assignments: [],
      outputVariables: {},
      mergeWithInput: false,
    };
    const nodes: WorkflowNode[] = [executeNode];

    const datasetId = this.schemaService.getWorkflowSchema(containerDataSource.id)?.tableId;
    const actionNodes = this.getBulkActionNode(actionType, datasetId, element);
    if (actionNodes) {
      executeNode.hasActionNode = true;
      if (Array.isArray(actionNodes)) {
        executeNode.nextNodeId = actionNodes[0].id;
        nodes.push(...actionNodes);
      } else {
        executeNode.nextNodeId = actionNodes.id;
        nodes.push(actionNodes);
      }
    }
    return nodes;
  }

  private getBulkActionNode(
    actionType: BulkActionType,
    datasetId: string | undefined,
    element: CanvasElement | undefined,
  ):
    | undefined
    | [SaveFileWorkflowNode, DownloadWorkflowNode]
    | [UpdateWorkflowNode]
    | DeleteWorkflowNode {
    const nodeContext = {
      datasetId,
      id: BULK_ACTION_NODE_ID,
      isBulkActionNode: true,
      isPreviousNodeGetNode: false,
      isServerWorkflow: false,
    };
    switch (actionType) {
      case 'export':
        return this.createNodesForExport(element);
      case 'delete':
        return createWorkflowNode('Delete', nodeContext) as DeleteWorkflowNode;
      case 'edit':
        return [
          {
            ...(createWorkflowNode('Update', nodeContext) as UpdateWorkflowNode),
            bulkEdit: true,
          },
        ];
      default:
        return undefined;
    }
  }

  /** Exporting writes the picked rows to a file and then hands that file to the browser. */
  private createNodesForExport(
    element: CanvasElement | undefined,
  ): [SaveFileWorkflowNode, DownloadWorkflowNode] {
    const downloadNodeId = `${BULK_ACTION_NODE_ID}_download`;
    // A table exports the columns it shows; anything else exports whatever the rows carry.
    const columns = isElementType(element, 'table')
      ? element.props.content.columns
          .filter((col) => col.visible)
          .map((col) => col.id ?? col.name)
          .filter(Boolean)
      : [];

    return [
      {
        ...getWorkflowNodeBase('SaveFile', BULK_ACTION_NODE_ID),
        fileType: 'Csv',
        chooseColumns: columns.length > 0,
        filename: 'exported-data',
        columns,
        uploadToS3: true,
        nextNodeId: downloadNodeId,
      } as SaveFileWorkflowNode,
      {
        ...getWorkflowNodeBase('Download', downloadNodeId),
        url: '$$input1["downloadUrl"]',
        displayInfos: [getInput1DisplayInfo('downloadUrl')],
      } as DownloadWorkflowNode,
    ];
  }
}
